import { END, buffers, eventChannel } from 'redux-saga';
import { all, call, delay, put, take, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  NO_KIOSK_MEDIA_ACCESS,
  RichMediaApiError,
  arrangeRichMedia,
  getKioskMediaAccess,
  getLocationRichMedia,
  getRichMediaCategories,
  removeRichMedia,
  syncRichMedia,
  uploadRichMedia,
  type KioskMediaAccess,
  type LocationRichMediaResponse,
  type MediaSyncResponse,
  type RichMediaCategory,
  type UploadRichMediaResponse,
} from '../../services/richMediaApi';
import * as T from './mediaManagementTypes';
import * as A from './mediaManagementActions';

const TOAST_MS = 3600;

const NO_PERMISSION = "You don't have permission";

const messageOf = (error: unknown, fallback: string) => {
  if (error instanceof RichMediaApiError && error.status === 403) return NO_PERMISSION;
  return error instanceof Error && error.message ? error.message : fallback;
};

/* ---------- Load ---------- */

function* loadMedia(locationId: string) {
  try {
    const data: LocationRichMediaResponse = yield call(getLocationRichMedia, locationId);
    yield put(A.loadMediaSuccess(locationId, data));
  } catch (error) {
    yield put(A.loadMediaFailure(locationId, messageOf(error, 'Failed to load media')));
  }
}

function* openLocationSaga(action: ReturnType<typeof A.openLocation>) {
  const { locationId } = action.payload;
  if (!locationId) return;
  yield put(A.loadMediaRequest(locationId));
}

function* loadMediaSaga(action: ReturnType<typeof A.loadMediaRequest>) {
  yield call(loadMedia, action.payload.locationId);
}

function* loadCategoriesSaga(action: ReturnType<typeof A.loadCategoriesRequest>) {
  const { locationId } = action.payload;
  try {
    const categories: RichMediaCategory[] = yield call(getRichMediaCategories, locationId);
    yield put(A.loadCategoriesSuccess(locationId, categories));
  } catch (error) {
    yield put(A.loadCategoriesFailure(locationId, messageOf(error, 'Failed to load categories')));
  }
}

/* ---------- Save ---------- */

type UploadEvent = { progress: number } | { done: UploadRichMediaResponse } | { error: unknown };
type UploadChannel = { close: () => void };

/**
 * Runs one upload and reports its progress as channel events. Buffered, so no event (above all the
 * final done/error) is lost while the saga is busy dispatching the previous progress.
 */
function uploadChannel(params: Parameters<typeof uploadRichMedia>[0]): UploadChannel {
  return eventChannel((emit: (event: UploadEvent | typeof END) => void) => {
    uploadRichMedia({ ...params, onProgress: (progress) => emit({ progress }) })
      .then((done) => emit({ done }))
      .catch((error) => emit({ error }))
      .finally(() => emit(END));
    return () => undefined;
  }, buffers.expanding(10));
}

function* upload(locationId: string, owner: string, change: T.SaveChange) {
  const channel: UploadChannel = yield call(uploadChannel, {
    locationId,
    div: change.div,
    itemId: owner === T.LOCATION_OWNER ? null : owner,
    file: change.file as File,
  });
  try {
    while (true) {
      const event: UploadEvent = yield take(channel);
      if ('progress' in event) yield put(A.setSlotProgress(change.key, event.progress));
      else if ('error' in event) throw event.error;
      else return event.done;
    }
  } finally {
    channel.close();
  }
}

/** One upload or removal. Returns the error message, or null when it was saved. */
function* saveChange(locationId: string, owner: string, change: T.SaveChange) {
  yield put(A.setSlotProgress(change.key, 0));
  try {
    if (change.kind === 'remove') {
      if (change.mediaId) yield call(removeRichMedia, locationId, change.mediaId);
    } else if (change.file) {
      yield call(upload, locationId, owner, change);
    }
    return null;
  } catch (error) {
    const message = messageOf(error, 'Save failed');
    yield put(A.addMediaToast(`${change.label}: ${message}`, 'error'));
    return message;
  } finally {
    yield put(A.setSlotProgress(change.key, null));
  }
}

/**
 * Moved files first, in one arrange call (P5) for the outlet or the item, so the backend slots match
 * what is shown; then every upload and removal in parallel (P2/P3) into the slots they are shown in;
 * then P1 again. A failed call keeps its change staged with the error; a failed arrange moves nothing
 * and stops the save.
 */
function* saveMediaSaga(action: ReturnType<typeof A.saveMediaRequest>) {
  const { requestId, locationId, owner, placements, changes } = action.payload;
  let changedAny = false;

  if (placements.length) {
    const keys = placements.map((p) => T.slotKey(owner, p.div));
    for (const key of keys) yield put(A.setSlotProgress(key, 0));
    try {
      const isLocation = owner === T.LOCATION_OWNER;
      yield call(arrangeRichMedia, {
        locationId,
        entityType: isLocation ? 'LOCATION' : 'ITEM',
        itemId: isLocation ? null : owner,
        media: placements,
      });
      changedAny = true;
    } catch (error) {
      yield put(A.addMediaToast(messageOf(error, 'Could not change the positions. Try again.'), 'error'));
      // Show what the backend has (unchanged); staged files stay staged.
      yield call(loadMedia, locationId);
      yield put(A.saveMediaDone({ requestId, owner, doneKeys: [], failed: {}, ok: false }));
      return;
    } finally {
      for (const key of keys) yield put(A.setSlotProgress(key, null));
    }
  }

  const results: Array<string | null> = yield all(changes.map((change) => call(saveChange, locationId, owner, change)));
  const doneKeys: string[] = [];
  const failed: Record<string, string> = {};
  results.forEach((error, i) => {
    if (error) failed[changes[i].key] = error;
    else doneKeys.push(changes[i].key);
  });

  if (changedAny || doneKeys.length) yield call(loadMedia, locationId);
  yield put(A.saveMediaDone({ requestId, owner, doneKeys, failed, ok: Object.keys(failed).length === 0 }));
}

/* ---------- Sync ---------- */

function* syncMediaSaga(action: ReturnType<typeof A.syncMediaRequest>) {
  const { locationId } = action.payload;
  try {
    const res: MediaSyncResponse = yield call(syncRichMedia, locationId);
    if (!res.published) {
      yield put(A.syncMediaFailure());
      yield put(A.addMediaToast('Could not notify Maghil Menu devices. Try syncing again.', 'error'));
      return;
    }
    yield put(A.syncMediaSuccess());
    yield put(A.addMediaToast('Sync triggered successfully', 'success', 'bottom'));
  } catch (error) {
    yield put(A.syncMediaFailure());
    yield put(A.addMediaToast(messageOf(error, 'Sync failed. Try again.'), 'error'));
  }
}

/* ---------- Access ---------- */

/** A failed read (e.g. 403 for a role without "Employee details") counts as no access. */
function* loadAccessSaga() {
  try {
    const access: KioskMediaAccess = yield call(getKioskMediaAccess);
    yield put(A.loadMediaAccessDone(access));
  } catch {
    yield put(A.loadMediaAccessDone(NO_KIOSK_MEDIA_ACCESS));
  }
}

/* ---------- Toasts ---------- */

function* autoDismissToastSaga(action: ReturnType<typeof A.addMediaToast>) {
  yield delay(TOAST_MS);
  yield put(A.dismissMediaToast(action.payload.id));
}

export default function* mediaManagementSaga() {
  yield takeLatest(T.OPEN_LOCATION, openLocationSaga);
  yield takeLatest(T.LOAD_MEDIA_REQUEST, loadMediaSaga);
  yield takeLatest(T.LOAD_CATEGORIES_REQUEST, loadCategoriesSaga);
  yield takeEvery(T.SAVE_MEDIA_REQUEST, saveMediaSaga);
  yield takeLatest(T.SYNC_MEDIA_REQUEST, syncMediaSaga);
  yield takeLatest(T.LOAD_ACCESS_REQUEST, loadAccessSaga);
  yield takeEvery(T.ADD_TOAST, autoDismissToastSaga);
}
